// External
import React, { useEffect, useRef, useState } from 'react';
import { string, node, oneOf } from 'prop-types';
import cn from 'classnames';

// Constants
const classname = 'tooltip';

/**
 * @name ToolTip
 * @description add styled tooltip to content
 * @param {string} [className] append class name
 * @param {string} title string to use as tooltip message
 * @param {positions} [position=bottom] - The position of the tooltip
 * @typedef positions {('top'|'bottom'|'left'|'right)}
 * @example
 * <ToolTip title={'my title'} position='left'/>
 */
const ToolTip = ({ title, position, children, className }) => {
  // Instance
  const node = useRef();
  const derivedClassname = cn(className, classname);

  // State
  const [isVisible, setVisible] = useState(false);

  // Funcs
  const handleHover = () => {
    setVisible(prev => !prev);
  };

  // Lifecycles
  useEffect(() => {
    const currentNode = node.current;
    if (currentNode) {
      currentNode.addEventListener('mouseover', handleHover);
      currentNode.addEventListener('mouseout', handleHover);
    }
    return () => {
      if (currentNode) {
        currentNode.removeEventListener('mouseover', handleHover);
        currentNode.addEventListener('mouseout', handleHover);
      }
    };
  }, []);

  return (
    <div className={derivedClassname} ref={node}>
      <div className={`${derivedClassname}__children`}>{children}</div>
      {isVisible && (
        <div title={title} className={cn(`${derivedClassname}__content`, position)}>
          <span className='arrow' />
          <p>{title}</p>
        </div>
      )}
    </div>
  );
};

ToolTip.defaultProps = {
  position: 'bottom',
  className: ''
};

ToolTip.propTypes = {
  title: string.isRequired,
  position: oneOf(['top', 'bottom', 'left', 'right']),
  children: node.isRequired,
  className: string
};

export default ToolTip;
